import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import PublicSiteShell from "../PublicSiteShell";
import homepageFallback from "../../../content/publicHome/homepageFallback";
import { loadHomepageContent } from "../../../lib/sanity/homepage";
import { SITE_ORIGIN } from "../../../seo/routeSeo";
import HomeAudienceSplit from "./HomeAudienceSplit";
import HomeFaq from "./HomeFaq";
import HomeFinalCta from "./HomeFinalCta";
import HomeHero from "./HomeHero";
import HomeMission from "./HomeMission";
import HomeProcessStory from "./HomeProcessStory";
import HomeProofStrip from "./HomeProofStrip";
import HomeSystemShowcase from "./HomeSystemShowcase";
import HomeTrustArchitecture from "./HomeTrustArchitecture";
import HomeWhySection from "./HomeWhySection";

function buildStructuredData(homepage) {
  const faqItems = homepage.faqModule?.items || [];

  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Experts4Europe",
      url: SITE_ORIGIN,
      description: homepage.seoModule.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Experts4Europe",
      url: SITE_ORIGIN,
      inLanguage: "de",
    },
    faqItems.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null,
  ].filter(Boolean);
}

export default function HomePage() {
  const [homepage, setHomepage] = useState(homepageFallback);

  useEffect(() => {
    let active = true;

    loadHomepageContent().then((result) => {
      if (active) {
        setHomepage(result.data);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  const structuredData = useMemo(() => buildStructuredData(homepage), [homepage]);
  const canonicalHref = `${SITE_ORIGIN}${homepage.seoModule?.canonicalPath || "/"}`;

  const hero = <HomeHero content={homepage.heroModule} />;

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{homepage.seoModule.title}</title>
        <meta name="description" content={homepage.seoModule.description} />
        <meta property="og:title" content={homepage.seoModule.ogTitle || homepage.seoModule.title} />
        <meta property="og:description" content={homepage.seoModule.ogDescription || homepage.seoModule.description} />
        <link rel="canonical" href={canonicalHref} />
        {structuredData.map((item, index) => (
          <script key={`homepage-ld-${index}`} type="application/ld+json">
            {JSON.stringify(item)}
          </script>
        ))}
      </Helmet>

      <PublicSiteShell pageClassName="public-home-premium" topBandTone="hero" hero={hero}>
        <HomeProofStrip content={homepage.proofStripModule} />
        <HomeWhySection content={homepage.benefitCardsModule} />
        <HomeProcessStory content={homepage.processStoryModule} />
        <HomeSystemShowcase content={homepage.systemShowcaseModule} />
        <HomeAudienceSplit content={homepage.audienceSplitModule} />
        <HomeMission content={homepage.missionModule} />
        <HomeTrustArchitecture content={homepage.trustModule} />
        <HomeFaq content={homepage.faqModule} />
        <HomeFinalCta content={homepage.ctaModule} />
      </PublicSiteShell>
    </>
  );
}
